import './App.css';
import { Routes, Route } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
const Home = lazy(() => import('./pages/Home.js'));
const FinalOperativeReport = lazy(() => import('./features/reports/pages/FinalOperativeReport.jsx'));
const Login = lazy(() => import('./features/auth/pages/LoginPage.jsx'));
const Unauthorized = lazy(() => import('./features/auth/pages/Unauthorized.jsx'));
const ProtectedRoute = lazy(() => import('./components/common/ProtectedRoute.jsx'));
const ForgotPassword = lazy(() => import('./features/auth/pages/ForgotPassword.jsx'));
const ResetPassword = lazy(() => import('./features/auth/pages/ResetPassword.jsx'));
const RemoteSignature = lazy(() => import('./components/common/RemoteSignature.jsx'));
const UserPage = lazy(() => import('./features/users/pages/UserPage.jsx'));
const UserListPage = lazy(() => import('./features/users/pages/UserListPage.jsx'));
const UserAuditLog = lazy(() => import('./features/users/pages/UserAuditLog.jsx'));
const RolePage = lazy(() => import('./features/roles/pages/RolePage.jsx'));
const RoleListPage = lazy(() => import('./features/roles/pages/RoleListPage.jsx'));
const PermissionManagement = lazy(() => import('./features/roles/pages/PermissionManagement.jsx'));
const PatientPage = lazy(() => import('./features/patients/pages/PatientPage.jsx'));
const PatientListPage = lazy(() => import('./features/patients/pages/PatientListPage.jsx'));
const MainPage = lazy(() => import('./pages/MainPage.js'));
const InventoryPage = lazy(() => import('./features/inventory/pages/InventoryPage.jsx'));
const InventoryListPage = lazy(() => import('./features/inventory/pages/InventoryListPage.jsx'));
const OTBooking = lazy(() => import('./features/bookings/pages/BookingListPage.jsx'));
const OTBookingPage = lazy(() => import('./features/bookings/pages/BookingPage.jsx'));
const OTRooms = lazy(() => import('./features/ot-management/pages/OTRooms.jsx'));
const OTRoomMaster = lazy(() => import('./features/ot-management/pages/OTRoomMaster.jsx'));
const DoctorMaster = lazy(() => import('./features/ot-management/pages/DoctorMaster.jsx'));
const SurgeryTypeMaster = lazy(() => import('./features/ot-management/pages/SurgeryTypeMaster.jsx'));
const OTReports = lazy(() => import('./features/ot-management/pages/OTReports.jsx'));
const AuditCenter = lazy(() => import('./features/administration/pages/AuditCenter.jsx'));
const FeedbackPage = lazy(() => import('./features/feedback/pages/FeedbackPage.jsx'));
const FeedbackListPage = lazy(() => import('./features/feedback/pages/FeedbackListPage.jsx'));
const VideoRecorder = lazy(() => import('./components/common/videorecorder.jsx'));
const VoiceRecorder = lazy(() => import('./components/common/voicerecorder.jsx'));
const AddFile = lazy(() => import('./features/addfile/pages/AddFilePage.jsx'));
const ConsentForAnaestnesia = lazy(() => import('./features/preoperative/pages/ConsentForAnaesthesia'));
const ConsentForMedical = lazy(() => import('./features/preoperative/pages/ConsentForm'));
const SurgicalSafetyChecklist = lazy(() => import('./features/preoperative/pages/SurgicalSafetyChecklist'));
const HighRiskConsentForm = lazy(() => import('./features/preoperative/pages/HighRiskConsentForm'));
const PreOperativeChecklist = lazy(() => import('./features/preoperative/pages/PreOperativeChecklist'));
const ConfirmationPhase = lazy(() => import('./features/preoperative/pages/ConfirmationPhase'));
const PreoperativeOrders = lazy(() => import('./features/preoperative/pages/PreOperativeOrders'));
const PreAnaestheticForm = lazy(() => import('./features/preoperative/pages/PreAnaestheticForm'));
const IntraOperativeNursingRecord = lazy(() => import('./features/intraoperative/pages/IntraOperativeNursingRecord.js'));
const OperationSheet = lazy(() => import('./features/intraoperative/pages/OperationSheet.js'));
const IntraPostOperativeOrders = lazy(() => import('./features/postoperative/pages/PostOperativeOrders.jsx'));
const PostOperative = lazy(() => import('./features/postoperative/pages/PostOperativeHub.jsx'));
const AldreteScore = lazy(() => import('./features/postoperative/pages/AldreteScore.jsx'));
const PostOperativeHandover = lazy(() => import('./features/postoperative/pages/PostOperativeHandover.jsx'));
const PostOperativeInstructions = lazy(() => import('./features/postoperative/pages/PostOperativeInstructions.jsx'));
const PostOperativeTable = lazy(() => import('./features/postoperative/pages/AnalgesiaAssessmentChart.jsx'));
const ConsiderationPhase = lazy(() => import('./features/consideration/pages/ConsiderationPhase.js'));
const AnesthesiaForm = lazy(() => import('./features/preoperative/pages/AnesthesiaForm.jsx'));



// Public component

// User Management
// Role Management

// Patient Management
// Inventory Management
// OT Management
// Feedback Management

// Pre-operative



// Intra-operative


// Post-operative


// Other


function App() {
  return (
    <Suspense fallback={<div className="flex h-screen w-full items-center justify-center"><div className="text-slate-500 font-semibold">Loading Application Module...</div></div>}>
    <Routes>
      {/* Public Routes */}
      <Route path='/' element={<Login />} />
      <Route path='/login' element={<Login />} />
      <Route path='/unauthorized' element={<Unauthorized />} />

      <Route path="/forgot_password" element={<ForgotPassword />} />
      <Route path="/reset_password/:token" element={<ResetPassword />} />


      {/* Admin-only Routes */}
      <Route
        path='/addUser'
        element={
          <ProtectedRoute allowedPermissions={['Users Add']}>
            <UserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/editUser/:user_id'
        element={
          <ProtectedRoute allowedPermissions={['Users Edit']}>
            <UserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/user'
        element={
          <ProtectedRoute allowedPermissions={['Users View']}>
            <UserListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/userAuditLog'
        element={
          <ProtectedRoute allowedPermissions={['Users View']}>
            <UserAuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path='/addRole'
        element={
          <ProtectedRoute allowedPermissions={['Roles Add']}>
            <RolePage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/editRole/:role_id'
        element={
          <ProtectedRoute allowedPermissions={['Roles Edit']}>
            <RolePage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/roleList'
        element={
          <ProtectedRoute allowedPermissions={['Roles View']}>
            <RoleListPage />
          </ProtectedRoute>
        }
      />

      {/* Shared Protected Routes (Admin + Nurse) */}
      <Route
        path='/home'
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Nurse', 'OT Manager', 'Doctor', 'Staff']}>
            <Home />
          </ProtectedRoute>
        }
      />
            <Route
        path='/voice'
        element={
          <ProtectedRoute allowedPermissions={['Dashboard View']}>
            <VoiceRecorder />
          </ProtectedRoute>
        }
      />
      <Route
        path='/video'
        element={
          <ProtectedRoute allowedPermissions={['Dashboard View']}>
            <VideoRecorder />
          </ProtectedRoute>
        }
      />
      <Route
        path='/signature-form/:uhid/:token'
        element={
          <ProtectedRoute allowedPermissions={['Consents Edit']}>
            <RemoteSignature />
          </ProtectedRoute>
        }
      />

      <Route
        path='/addPatient'
        element={
          <ProtectedRoute allowedPermissions={['Patients Add']}>
            <PatientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/editPatient/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Patients Edit']}>
            <PatientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/listPatient'
        element={
          <ProtectedRoute allowedPermissions={['Patients View']}>
            <PatientListPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/preoperativeorders/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Pre-Op Orders View']}>
            <PreoperativeOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path='/preInductionAssessment/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Pre-Anaesthetic Evaluation View']}>
            <AnesthesiaForm />
          </ProtectedRoute>
        }
      />
      <Route
        path='/mainPage/:uhid_no'
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Nurse', 'OT Manager', 'Doctor', 'Staff']}>
            <MainPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/addInventory'
        element={
          <ProtectedRoute allowedPermissions={['Inventory Add']}>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/editInventory/:item_id'
        element={
          <ProtectedRoute allowedPermissions={['Inventory Edit']}>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/listInventory'
        element={
          <ProtectedRoute allowedPermissions={['Inventory View']}>
            <InventoryListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/otRooms'
        element={
          <ProtectedRoute allowedPermissions={['OT Rooms View']}>
            <OTRooms />
          </ProtectedRoute>
        }
      />
      <Route
        path='/otRoomMaster'
        element={
          <ProtectedRoute allowedPermissions={['OT Rooms View']}>
            <OTRoomMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path='/doctorMaster'
        element={
          <ProtectedRoute allowedPermissions={['Dashboard View']}>
            <DoctorMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path='/surgeryTypeMaster'
        element={
          <ProtectedRoute allowedPermissions={['Dashboard View']}>
            <SurgeryTypeMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path='/otReports'
        element={
          <ProtectedRoute allowedPermissions={['Dashboard View']}>
            <OTReports />
          </ProtectedRoute>
        }
      />
      <Route
        path='/auditCenter'
        element={
          <ProtectedRoute allowedPermissions={['Users View']}>
            <AuditCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path='/otBooking'
        element={
          <ProtectedRoute allowedPermissions={['OT Booking View']}>
            <OTBooking />
          </ProtectedRoute>
        }
      />

      <Route
        path='/addOtBooking'
        element={
          <ProtectedRoute allowedPermissions={['OT Booking Add']}>
            <OTBookingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/editOtBooking/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['OT Booking Edit']}>
            <OTBookingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/addFeedback'
        element={
          <ProtectedRoute allowedPermissions={['Patient Feedback Add']}>
            <FeedbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/listFeedback'
        element={
          <ProtectedRoute allowedPermissions={['Patient Feedback View']}>
            <FeedbackListPage />
          </ProtectedRoute>
        }
      />

      {/* Pre-Operative Routes */}

      <Route
        path='/consentForAnaesthesia/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Consents View']}>
            <ConsentForAnaestnesia />
          </ProtectedRoute>
        }
      />
      <Route
        path='/consentForMedical/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Consents View']}>
            <ConsentForMedical />
          </ProtectedRoute>
        }
      />
      <Route
        path='/surgicalSafetyChecklist/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Surgical Safety Checklist View']}>
            <SurgicalSafetyChecklist />
          </ProtectedRoute>
        }
      />
      <Route
        path='/preAnaestheticForm/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Pre-Anaesthetic Evaluation View']}>
            <PreAnaestheticForm />
          </ProtectedRoute>
        }
      />
      {/* <Route
      //   path='/preAnaestheticForm/:uhid_no'
      //   element={
      //     <ProtectedRoute allowedRoles={['Admin', 'Nurse', 'OT Manager', 'Doctor', 'Staff']}>
      //       <PreAnaestheticForm />
      //     </ProtectedRoute>
      //   }
      // />
      <Route
        path='/2'
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Nurse', 'OT Manager', 'Doctor', 'Staff']}>
            <PreAnaestheticForm2 />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path='/highRiskConsentForm/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Consents View']}>
            <HighRiskConsentForm />
          </ProtectedRoute>
        }
      />
      <Route
        path='/preOperativeChecklist/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Pre-Op Checklist View']}>
            <PreOperativeChecklist />
          </ProtectedRoute>
        }
      />
      <Route
        path='/confirmationPhase/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Pre-Op Checklist View']}>
            <ConfirmationPhase />
          </ProtectedRoute>
        }
      />

      {/* Intra-Operative Routes */}

      <Route
        path='/intraOperativeNursingRecord/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Nursing Record View']}>
            <IntraOperativeNursingRecord />
          </ProtectedRoute>
        }
      />
      <Route
        path='/operationSheet/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Operation Sheet View']}>
            <OperationSheet />
          </ProtectedRoute>
        }
      />
      <Route
        path='/postOperativeOrders/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Post-Op Orders View']}>
            <IntraPostOperativeOrders />
          </ProtectedRoute>
        }
      />

      {/* Post-Operative Routes */}
      <Route
        path='/postOperative/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Post-Op Orders View']}>
            <PostOperative />
          </ProtectedRoute>
        }
      />
      <Route
        path='/aldreteScore/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Alderete Score View']}>
            <AldreteScore />
          </ProtectedRoute>
        }
      />
      <Route
        path='/postOperativeHandover/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Post-Op Handover View']}>
            <PostOperativeHandover />
          </ProtectedRoute>
        }
      />
      <Route
        path='/postOperativeInstructions/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Post-Op Instructions View']}>
            <PostOperativeInstructions />
          </ProtectedRoute>
        }
      />
      <Route
        path='/postOperativeAssesment/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Post-Op Instructions View']}>
            <PostOperativeTable />
          </ProtectedRoute>
        }
      />
      <Route
        path='/addfile/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Patients Edit']}>
            <AddFile />
          </ProtectedRoute>
        }
      />


      <Route
        path='/considerationPhase/:uhid_no'
        element={
          <ProtectedRoute allowedPermissions={['Consents View']}>
            <ConsiderationPhase />
          </ProtectedRoute>
        }
      />
      <Route
        path='/roleManagement'
        element={
          <ProtectedRoute allowedPermissions={['Roles View']}>
            <PermissionManagement />
          </ProtectedRoute>
        }
      />
      {/* Catch-all route redirects to login */}
      <Route path="*" element={<Login />} />
          <Route path='/finalOperativeReport/:uhid_no' element={<FinalOperativeReport />} />
    </Routes>
    </Suspense>
  );
}

export default App;
